import { useState } from "react"
import {
  PlusIcon,
  EyeIcon,
  PhotoIcon,
} from "@heroicons/react/24/outline"
import { Modal } from "../../components/modal/Modal"
import { Media } from "./types"
import { Section } from "./ui"

/* ===================== HELPERS ===================== */

const withHttps = (url?: string) => {
  if (!url) return ""
  if (/^https?:\/\//i.test(url)) return url
  return `https://${url}`
}

/* ===================== COMPONENT ===================== */

export const PortfolioBlock = ({
  items,
  onUpload,
}: {
  items: Media[]
  onUpload: (file: File) => Promise<void>
}) => {
  const [openUpload, setOpenUpload] = useState(false)
  const [previewItem, setPreviewItem] = useState<Media | null>(null)
  const [uploading, setUploading] = useState(false)

  return (
    <>
      <Section
        id="portfolio"
        title="Portfolio"
        icon={PhotoIcon}
        iconColor="text-amber-500 bg-amber-50"
        description="Screenshots, designs and projects you’ve worked on"
        onAdd={() => setOpenUpload(true)}
      >
        {items.length === 0 ? (
          <EmptyState onAdd={() => setOpenUpload(true)} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((item) => {
              const thumb =
                item.original_url ||
                item.metadata?.thumb_small_url

              return (
                <button
                  key={item.id}
                  onClick={() => setPreviewItem(item)}
                  className="group relative overflow-hidden rounded-2xl border bg-gray-100 aspect-[4/3]"
                >
                  <img
                    src={withHttps(thumb)}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />

                  {/* overlay */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition" />

                  {/* icon */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                    <div className="flex items-center gap-2 bg-white/90 backdrop-blur px-4 py-2 rounded-full text-sm font-medium">
                      <EyeIcon className="w-4 h-4" />
                      View
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </Section>

      {/* PREVIEW MODAL */}
      <Modal
        open={!!previewItem}
        onClose={() => setPreviewItem(null)}
        title="Portfolio preview"
      >
        {previewItem && (
          <div className="flex justify-center bg-gray-50 p-4 rounded-xl">
            <img
              src={withHttps(previewItem.original_url)}
              className="max-h-[75vh] rounded-xl shadow-lg object-contain"
            />
          </div>
        )}
      </Modal>

      {/* UPLOAD MODAL */}
      <Modal
        open={openUpload}
        onClose={() => setOpenUpload(false)}
        title="Add to portfolio"
      >
        <div className="space-y-4">
          <input
            type="file"
            accept="image/*"
            disabled={uploading}
            className="block w-full text-sm file:mr-4 file:rounded-lg file:border-0 file:bg-blue-600 file:px-4 file:py-2 file:text-white hover:file:bg-blue-700 disabled:opacity-50"
            onChange={async (e) => {
              const file = e.target.files?.[0]
              if (!file) return

              try {
                setUploading(true)
                await onUpload(file)
                setOpenUpload(false)
              } finally {
                setUploading(false)
                e.target.value = ""
              }
            }}
          />

          {uploading && (
            <div className="text-sm text-gray-400">
              Uploading…
            </div>
          )}
        </div>
      </Modal>
    </>
  )
}

/* ===================== EMPTY STATE ===================== */

const EmptyState = ({ onAdd }: { onAdd: () => void }) => (
  <div className="flex flex-col items-center justify-center gap-3 border border-dashed rounded-xl py-12 text-center text-gray-500">
    <PhotoIcon className="w-10 h-10 text-gray-300" />
    <div className="text-sm font-medium">No works yet</div>
    <div className="text-xs text-gray-400 max-w-xs">
      Upload screenshots, designs or projects to showcase your experience
    </div>

    <button
      onClick={onAdd}
      className="mt-2 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 transition"
    >
      <PlusIcon className="w-4 h-4" />
      Add first work
    </button>
  </div>
)
